import {
  DEVICE,
  NO_SPIN_REASON_LOW_BALL_SPIN_COUNT,
  NO_SPIN_REASON_ROTOR_AND_BALL_SAME_DIRECTION,
  NO_SPIN_REASON_BALL_NOT_IN_THE_WHEEL,
  NO_SPIN_REASON_UNKNOWN,
  AMOUNT_TYPES,
  ERRORS,
} from '@ezugi/constants';
import { GameError, Notification } from '@ezugi/types';
import { formatAmount, DeviceInfo } from '@ezugi/utils';

import { errorActions, notificationActions, userActions } from '../../actions';
import { formatSelector, minBalanceSelector, userCurrencySelector } from '../../selectors';
import { RootState } from '../../types';

const {
  CODES, INSUFFICIENT_FUNDS_FOR_ENTRY, TABLE_IS_CLOSED, TABLE_OCCUPIED, CURRENCY_MISMATCH,
} = ERRORS;

const { TYPE: { TABLET, SMARTPHONE, HTML } } = DEVICE;

export const getClientType = () => (DeviceInfo.isTablet && TABLET) || (DeviceInfo.isMobile && SMARTPHONE) || HTML;

export const pluckSocketParams = (state: any, extraParameters: any) => {
  const { fromReconnectToServer } = extraParameters;
  const {
    config: { definitions, authParams, isEvoWrapped },
    user,
    game: { limitId },
    /* eslint-disable no-shadow */
    game,
    table: { externalTableName, externalTableId },
  } = state;
  const url = definitions.SERVER_URL_GAMES;
  const ClientId = `${user.operatorId}|${user.uid}|${authParams.table_id}`;
  const params = {
    MessageType: 'INITIALIZE_PLAYER',
    TableId: authParams.table_id,
    LimitId: limitId,
    SessionCurrency: user.currency,
    CurrentPlayerToken: user.currentPlayerToken,
    destination: 'gameEngine',
    gameType: game.gameId,
    Language: 'ENGLISH',
    ClientIp: user.clientIp,
    ClientId,
    Nickname: user.nickname,
    OperatorID: user.operatorId,
    clientType: getClientType(),
    ...(isEvoWrapped && {
      ExternalTableId: externalTableId,
      ExternalTableName: externalTableName,
    }),
    ...(fromReconnectToServer && {
      fromReconnectToServer: true,
    }),
  };

  return {
    url,
    params,
  };
};

const { error } = errorActions;
const { notification } = notificationActions;
const { user } = userActions;

export const getPayload = (code: number, state: RootState) => {
  switch (true) {
  case code === 30008: {
    return {
      code,
    };
  }
  case code === 30037: {
    return {
      code,
    };
  }
  case code === 30055: {
    return {
      code: CODES[TABLE_IS_CLOSED],
      message: 'Table is closed',
    };
  }
  case code === 30083: {
    return {
      code: CODES[TABLE_OCCUPIED],
      title: 'errors.table_occupied_title',
      description: 'errors.table_occupied_description',
    };
  }
  case code === 30088: {
    return {
      code: CODES[CURRENCY_MISMATCH],
      title: 'messages.currency_mismatch_title',
      description: 'messages.currency_mismatch_description',
    };
  }
  case code === 30084: {
    let minBalance: string | number = 0;
    try {
      minBalance = formatAmount(
        minBalanceSelector(state),
        AMOUNT_TYPES.LIMIT,
        formatSelector(state),
        userCurrencySelector(state),
        false,
        true
      );
    } catch (_) {
      minBalance = minBalanceSelector(state) as number;
    }

    return {
      code: CODES[INSUFFICIENT_FUNDS_FOR_ENTRY],
      title: 'errors.insufficient_funds_for_entry_title',
      description: 'errors.insufficient_funds_for_entry_description',
      variables: {
        minBalance,
      },
    };
  }
  case code === 30086: {
    return { message: 'messages.maximum_seats_exceeded' };
  }
  case code === 40099: {
    return { message: 'messages.table_will_be_restarted' };
  }
  case code >= 30000 && code < 60000: {
    return { message: 'messages.bets_not_placed_message' };
  }
  case code === 90002: {
    return {
      code,
      title: 'errors.title_90002',
      description: 'errors.please_restart_table',
    };
  }
  default: {
    return { code, title: `errors.unknown_${code}`, description: `errors.unknown_${code}` };
  }
  }
};

export const getActions = (code: number, state: RootState) => {
  const fatal = [ 30008, 30037, 30055, 30083, 30084, 30088 ];

  if (fatal.includes(code)) return error.set(getPayload(code, state) as GameError);

  if (code >= 30000 && code < 60000) {
    return (
      notification.add(getPayload(code, state) as Notification),
      user.updateRealtimeBalanceWhenMainBetUnderLimit()
    );
  }

  return error.set(getPayload(code, state) as GameError);
};

const noSpinMessageMap = {
  [NO_SPIN_REASON_UNKNOWN]: 'roulette_ui.no_spin_unknown_reason',
  [NO_SPIN_REASON_LOW_BALL_SPIN_COUNT]: 'roulette_ui.no_spin_low_ball_spin_count_message',
  [NO_SPIN_REASON_BALL_NOT_IN_THE_WHEEL]: 'roulette_ui.no_spin_ball_not_in_the_wheel',
  [NO_SPIN_REASON_ROTOR_AND_BALL_SAME_DIRECTION]: 'roulette_ui.no_spin_rotor_and_ball_same_direction',
} as Record<string, string>;

export const getNoSpinAction = ({ NoSpinReason }: { NoSpinReason: string }) => notification.add({
  message: noSpinMessageMap[NoSpinReason] || noSpinMessageMap[NO_SPIN_REASON_UNKNOWN],
});
